import { ApolloClient, ApolloLink, InMemoryCache } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { setContext } from "@apollo/client/link/context";
import { env } from "../utils/env";
import { createUploadLink } from "apollo-upload-client";
import omitDeep from "omit-deep-lodash";
import { getUserManager } from "@axa-fr/react-oidc-core";
import i18next from "i18next";
import { getTenantId } from "providers/TenantProvider";
import { LANGUAGES_QUERY } from "utils/languages";
import { emptyObject } from "constants/defaultValues";

let access_token;
export function setAccessToken(accessToken) {
  access_token = accessToken;
}

const httpLink = createUploadLink({
  uri: `${env.REACT_APP_GQL_HTTP_PROTOCOL}://${env.REACT_APP_GQL}/graphql`,
  onError: onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors)
      graphQLErrors.map(({ message, locations, path }) =>
        console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
      );
    if (networkError) console.log(`[Network error]: ${networkError}`);
  }),
});

const getToken = async () => {
  const userManager = getUserManager();
  const user = await userManager.getUser();
  if (user?.access_token) return user.access_token;

  return access_token;
};

const authLink = setContext(async (_, { headers }) => {
  const { languages } = cache.readQuery({ query: LANGUAGES_QUERY }) || emptyObject;
  const language = languages?.find((x) => x.code === i18next.language);

  const tid = getTenantId();
  const baseHeaders = { ...headers, "x-tenantid": tid };

  if (language?.culture) baseHeaders["Accept-Language"] = language.culture;
  if (env.REACT_APP_USE_CIAM?.toLowerCase() === "true") return { headers: baseHeaders };

  const token = await getToken();
  const authorization = token ? `Bearer ${token}` : "";
  return { headers: { ...baseHeaders, authorization } };
});

const omitTypenameLink = new ApolloLink((operation, forward) => {
  if (operation.variables) {
    operation.variables = omitDeep(operation.variables, ["__typename"]);
  }
  return forward(operation);
});

const retryLink = new RetryLink({ delay: { initial: 200, max: 2000, jitter: true }, attempts: { max: 3 } });

const myAppLink = ApolloLink.from([omitTypenameLink, authLink, retryLink, httpLink]);

const cache = new InMemoryCache({
  typePolicies: {
    ApplicationParams: {
      keyFields: false,
    },
    ApplicationLoader: {
      keyFields: false,
    },
    Page: {
      keyFields: ["afterId", "sortBy", "direction", "pageSize"],
    },
    PartnerForCompanyData: {
      keyFields: false,
    },
    Quote: {
      keyFields: ["quoteId"],
    },
    QuoteAsset: {
      keyFields: ["quoteAssetId"],
    },
    QuoteProduct: {
      keyFields: ["quoteProductId"],
    },
    Asset: {
      keyFields: ["assetId"],
    },
    PriceCard: {
      keyFields: ["guid"],
    },
    ExchangeRate: {
      keyFields: false,
    },
    ListQuoteItem: {
      keyFields: ["quoteId"],
    },
    QuoteProductHistory: {
      keyFields: ["quoteProductId"],
    },
    LeasingPriceCard: {
      keyFields: ["priceCardId"],
    },
    File: {
      keyFields: false,
    },
    ProductSchedule: {
      keyFields: false,
    },
    ColumnDefinition: {
      keyFields: false,
    },
    ScheduleLine: {
      keyFields: false,
    },
    ScheduleLineValue: {
      keyFields: false,
    },
    InterestTypeDetail: {
      keyFields: false,
    },
    ContactPerson: {
      keyFields: ["webContactInfoId"],
    },
    DmsFile: {
      keyFields: ["sharePointUID"],
    },
    UserProfile: {
      keyFields: ["userId"],
    },
    Language: {
      keyFields: ["code"],
    },
    PublicDataCompletion: {
      keyFields: ["quoteId"],
    },
    PublicContractualDocuments: {
      keyFields: ["quoteId"],
    },
    ScoringQuestion: {
      keyFields: ["quoteId", "questionId"],
    },
    StringTranslations: {
      keyFields: ["languageCode"],
    },
    StringTranslation: {
      keyFields: ["key", "countryCode"],
    },
    RequiredDocument: {
      keyFields: ["quoteId", "id", "guarantorId"],
    },
    DealerDocument: {
      keyFields: ["dmsDocumentId"],
    },
  },
});

let apolloClient = null;
export const getApolloClient = () => {
  if (!apolloClient) {
    apolloClient = new ApolloClient({ link: myAppLink, cache });
  }
  return apolloClient;
};
